import { computed, ref } from 'vue'
import { searchPartsAcrossCollections } from '@/controllers/library/productPage'

// shared module state
const search = ref('')
const searchResults = ref<any[]>([])
const isSearching = ref(false)

let latestSearchRequest = 0
let searchDebounce: ReturnType<typeof setTimeout> | null = null

export function usePartCollectionSearch(
  productPageId: number,
  normalizePart: (part: any) => any
) {
  const isSearchMode = computed(() => search.value.trim().length > 0)

  async function runSearch(term: string, resetScroll?: () => void) {
    const requestId = ++latestSearchRequest
    isSearching.value = true

    try {
      const result = await searchPartsAcrossCollections(productPageId, term)

      if (requestId !== latestSearchRequest) return

      searchResults.value = (result ?? []).map((part: any) => normalizePart(part))
    } finally {
      if (requestId === latestSearchRequest) {
        isSearching.value = false
        resetScroll?.()
      }
    }
  }

  function clearSearchResults(resetScroll?: () => void) {
    latestSearchRequest++
    isSearching.value = false
    searchResults.value = []
    resetScroll?.()
  }

  function clearSearch(resetScroll?: () => void) {
    search.value = ''
    clearSearchResults(resetScroll)
  }

  function onSearchInput(event: Event, resetScroll?: () => void) {
    const term = (event.target as HTMLInputElement).value
    search.value = term

    if (searchDebounce) clearTimeout(searchDebounce)

    if (!term.trim()) {
      clearSearchResults(resetScroll)
      return
    }

    searchDebounce = setTimeout(() => {
      runSearch(term.trim(), resetScroll)
    }, 300)
  }

  function cleanup() {
    if (searchDebounce) {
      clearTimeout(searchDebounce)
      searchDebounce = null
    }
  }

  function resetSearchContext(resetScroll?: () => void) {
    cleanup()
    clearSearch(resetScroll)
  }

  return {
    search,
    searchResults,
    isSearching,
    isSearchMode,
    onSearchInput,
    clearSearch,
    clearSearchResults,
    cleanup,
    resetSearchContext,
    runSearch
  }
}
